import {
  Autocomplete,
  type AutocompleteCloseReason,
  Box,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchLoupe from 'assets/icons/search-loupe.svg';
import { defaultDebounceDelay } from 'common/constants';
import { SvgIconWrapper } from 'components/SvgIconWrapper';
import Fuse, { type IFuseOptions } from 'fuse.js';
import { isValuePresent } from 'helpers/utils';
import { debounce, isEmpty, orderBy } from 'lodash-es';
import { type FC, type SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import type { PrimaryIndustry, PrimaryIndustrySearchProps } from './PrimaryIndustrySearch.types';

const fuseOptions: IFuseOptions<PrimaryIndustry> = {
  minMatchCharLength: 2,
  useExtendedSearch: true,
  threshold: 0.6,
  ignoreLocation: true,
  fieldNormWeight: 0.2,
  keys: [
    {
      name: 'DisplayText',
      weight: 2,
    },
    {
      name: 'Synonyms',
      weight: 0.4,
    },
  ],
};

/**
 * Rules of the road for this component:
 * 1. If we haven't loaded industries or AI suggestions, disable the widget;
 * 2. If we have AI suggestions but the user hasn't typed anything, show the AI suggestions on top followed by everything else;
 * 3. If the user types in the autocomplete, run the fuzzy search and show the results in that order.
 */
const PrimaryIndustrySearch: FC<PrimaryIndustrySearchProps> = ({
  industries,
  formik,
  fieldName,
  placeholder,
  label,
  renderStartAdornment = true,
  isReadOnly = false,
  onChange,
  state,
  setState,
  suggestions,
  ...rest
}) => {
  const industriesWithSuggestions = useMemo<(PrimaryIndustry & { isSuggested?: boolean })[]>(
    () =>
      orderBy(
        industries.map((item: PrimaryIndustry) => ({
          ...item,
          isSuggested: suggestions?.includes(String(item.DisplayText)),
        })) ?? [],
        ['isSuggested'],
        ['desc'],
      ),
    [industries, suggestions],
  );
  const [options, setOptions] = useState(industriesWithSuggestions);

  const industrySearchRef = useRef<HTMLDivElement>(undefined);
  const [value, setValue] = useState(
    state ? options?.find((o) => o.DisplayText === state?.pol_tax_keyword) : undefined,
  );

  const fuse = new Fuse(industriesWithSuggestions, fuseOptions);

  /**
   * Updates the options state based on a search query.
   *
   * Creates a `subquery` by splitting the query string and joining the terms with ' | '.
   * Uses Fuse.js to perform a fuzzy search on the `industriesWithSuggestions` array, matching against the `DisplayText` and `Synonyms` fields.
   * Maps the search results to extract the `item` property and sets it as the new options.
   *
   * @param {string} query - The search query.
   */
  const setNewOptions = (query: string) => {
    const subquery = query.split(' ').join(' | ');
    const fuzzyResults = query
      ? fuse
          .search({
            $or: [
              { DisplayText: `"${query}"` },
              { DisplayText: query },
              { DisplayText: subquery },
              { Synonyms: query },
              { Synonyms: subquery },
            ],
          })
          ?.map((result) => result.item)
      : industriesWithSuggestions;

    setOptions(fuzzyResults);
  };

  /**
   * Handles the search input change event.
   *
   * Checks if the event type is 'change' and then calls `setNewOptions` with the new search value.
   * Debounces the search input to prevent excessive calls to the search function.
   *
   * @param {SyntheticEvent} event - The synthetic event triggered by user input.
   * @param {string} newValue - The new value entered by the user in the search field.
   */
  const handleSearch = async (event: SyntheticEvent, newValue: string) => {
    if (event?.type === 'change') {
      setNewOptions(newValue);
    }
  };

  /**
   * Handles the selection change event in the autocomplete component.
   *
   * Extracts the `DisplayText` from the selected option or sets it to an empty string if not available.
   * Updates the Formik field value and state with the new value.
   * Calls the optional `onChange` callback with the new and old values.
   *
   * @param {SyntheticEvent} _e - The synthetic event triggered by the selection change.
   * @param {PrimaryIndustry} selectedOption - The newly selected industry option.
   */
  const handleChange = (_e: SyntheticEvent, selectedOption: PrimaryIndustry) => {
    const val = selectedOption?.DisplayText ?? '';
    const oldVal = state?.pol_tax_keyword;

    formik.setFieldValue(fieldName, val);
    setState?.((prevState) => ({ ...prevState, pol_tax_keyword: val }));

    onChange?.(val, oldVal);
  };

  /**
   * Handles the autocomplete close event.
   *
   * Performs different actions based on the close reason:
   * - For `escape`, it blurs the input field and, if the `pol_tax_keyword` is empty, it resets options.
   * - For `blur`, if the state has a keyword value, it calls `setNewOptions` with the state's keyword value.
   *
   * These are for improving the UX while selecting industry keyword.
   *
   * @param {SyntheticEvent} _e - The synthetic event triggered by closing the autocomplete.
   * @param {AutocompleteCloseReason} closeReason - The reason for closing the autocomplete (e.g., 'escape', 'blur').
   */
  const handleClose = (_e: SyntheticEvent, closeReason: AutocompleteCloseReason) => {
    if (closeReason === 'escape') {
      const input = industrySearchRef?.current?.firstChild?.childNodes?.[0]
        ?.childNodes?.[1] as HTMLInputElement;

      input?.blur();

      if (isEmpty(state?.pol_tax_keyword)) {
        setOptions(industries);
      }
    }

    if (closeReason === 'blur' && isValuePresent(state?.pol_tax_keyword)) {
      setNewOptions(state?.pol_tax_keyword ?? '');
    }
  };

  useEffect(() => {
    // industriesWithSuggestions will often be empty at first boot, since we haven't loaded from the keyvalue store yet; but we need
    // to make sure to populate the list when it's ready so that values can be set externally (i.e. from AI suggestions)
    setOptions(industriesWithSuggestions);

    if (value) {
      // goofy edge-case for the full-screen mobile variant, where value exists on first render
      setNewOptions(value.DisplayText ?? '');
    }
  }, [industriesWithSuggestions, suggestions]);

  useEffect(() => {
    if (!value || state?.pol_tax_keyword !== value?.DisplayText) {
      setValue(state ? options?.find((o) => o.DisplayText === state?.pol_tax_keyword) : undefined);
    }
  }, [state?.pol_tax_keyword, options]);

  return (
    <Autocomplete
      {...rest}
      id={fieldName}
      ref={industrySearchRef}
      options={options}
      value={(value || null) as PrimaryIndustry}
      readOnly={isReadOnly}
      // still needed even w/ renderOption (for some reason, thanks MUI)
      onInputChange={debounce(handleSearch, defaultDebounceDelay)}
      onChange={handleChange}
      onClose={handleClose}
      getOptionLabel={(option: PrimaryIndustry) => `${option.DisplayText ?? ''}`}
      renderInput={(params) => (
        <TextField
          {...params}
          required
          name={fieldName}
          autoFocus={rest.autoFocus}
          label={label}
          placeholder={placeholder}
          error={formik.touched?.[fieldName] && Boolean(formik.errors?.[fieldName])}
          helperText={formik.touched?.[fieldName] && formik.errors?.[fieldName]}
          sx={[
            (theme) => theme.mixins.placeholderColor(formik, fieldName),
            (theme) => theme.mixins.errorIconStyle,
          ]}
          slotProps={{
            input: {
              ...params.InputProps,
              readOnly: isReadOnly,
              autoComplete: 'off',
              startAdornment: renderStartAdornment ? (
                <InputAdornment position="start">
                  <SvgIconWrapper
                    src={SearchLoupe}
                    className={
                      formik.touched?.[fieldName] && Boolean(formik.errors?.[fieldName])
                        ? 'error-icon-style'
                        : ''
                    }
                  />
                </InputAdornment>
              ) : null,
              endAdornment: null,
            },

            htmlInput: {
              ...params.inputProps,
              'data-test': fieldName,
            },

            inputLabel: isReadOnly ? { shrink: true } : {},
          }}
        />
      )}
      renderOption={(props, option) => (
        <Stack
          position="relative"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            {...props}
            key={option.DisplayValue}
            data-test={`industry-option-${option.DisplayText}`}
            sx={{ flex: 1 }}
          >
            {option.DisplayText}
          </Typography>
          {option?.isSuggested && (
            <Stack
              position="absolute"
              width="100%"
              height="100%"
              alignItems="flex-end"
              justifyContent="center"
              top={0}
              left={0}
              sx={{ pointerEvents: 'none' }}
            >
              <Box
                width="fit-content"
                sx={{ background: 'white', border: '1px solid #BB4F09' }}
                py={0.25}
                px={1}
                mr={2}
                borderRadius={5}
              >
                <Typography color="#BB4F09" variant="body2">
                  suggested
                </Typography>
              </Box>
            </Stack>
          )}
        </Stack>
      )}
      isOptionEqualToValue={(option, v) => option.DisplayText === v.DisplayText}
      openOnFocus
      filterSelectedOptions
      disableClearable
    />
  );
};

export default PrimaryIndustrySearch;
